<template>
  <standard-dialog
    v-model="model"
    :title="dialogTitle"
    :subtitle="editItem ? `ID: ${editItem.id}` : ''"
    :icon="editItem ? 'mdi-pencil' : 'mdi-plus-circle'"
    :save-text="editItem ? $t('save_changes') : $t('add_meal')"
    :cancel-text="$t('cancel')"
    save-icon="mdi-check"
    :primary-color="editItem ? 'primary' : 'success'"
    @save="submitMealForm"
    max-width="1000"
    :disabled="!valid"
  >
    <!-- LANGUAGE TABS -->
    <v-tabs v-model="activeLanguageTab" color="primary" class="mb-4">
      <v-tab v-for="lang in availableLanguages" :key="lang.code">
        {{ lang.text }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeLanguageTab">
      <v-tab-item v-for="lang in availableLanguages" :key="lang.code">
        <!-- OSNOVNI PODACI -->
        <v-form v-model="valid">
          <div class="section-card">
            <div class="section-header">
              <v-icon left small>mdi-food</v-icon>
              <span>{{ $t("basic_information") }}</span>
            </div>
            <div class="section-content">
              <template v-if="lang.code === 'default'">
                <text-field
                  v-if="!mealFormData.newSelectedName"
                  v-model="mealFormData.name"
                  :label="$t('name_menu')"
                  required
                  :rules="[rules.required]"
                  color="white"
                ></text-field>
                <text-field
                  v-else
                  v-model="mealFormData.newSelectedName"
                  :label="$t('name_menu')"
                  color="white"
                ></text-field>
              </template>
              <template v-else>
                <text-field
                  v-model="
                    mealFormData.translations.find((t) => t.code === lang.code).name
                  "
                  :label="$t('name_menu') + '(translate)'"
                  color="white"
                ></text-field>
              </template>
              <template v-if="lang.code === 'default'">
                <text-field
                  v-model="mealFormData.description"
                  :label="$t('description')"
                  hide-details
                  color="white"
                ></text-field>
              </template>
              <template v-else>
                <text-field
                  v-model="
                    mealFormData.translations.find((t) => t.code === lang.code)
                      .description
                  "
                  :label="$t('description') + '(translate)'"
                  hide-details
                  color="white"
                ></text-field>
              </template>

              <div class="price-row">
                <text-field
                  v-model="mealFormData.price"
                  :label="$t('price')"
                  color="white"
                  hide-details
                  :suffix="restaurant.settings.price"
                  clearable
                ></text-field>
                <text-field
                  :label="$t('quantity')"
                  hide-details
                  v-model="mealFormData.quantity"
                  color="white"
                ></text-field>
                <autocomplete
                  :label="$t('unit_of_measure')"
                  v-model="mealFormData.measure"
                  color="white"
                  :items="['kg', 'l', 'kom']"
                ></autocomplete>
              </div>
              <v-alert v-if="hasPriceError" dense outlined type="error" class="mt-2">
                {{ $t("price") }}: {{ $t("validation.required") }}
              </v-alert>
            </div>
          </div>

          <!-- Jela -->
          <div class="section-card">
            <div class="section-header">
              <v-icon left small>mdi-silverware-fork-knife</v-icon>
              <span>{{ $t("meals_main") }}</span>
            </div>
            <div class="section-content">
              <div class="meal-grid">
                <div
                  v-for="(food, index) in foods"
                  :key="index"
                  class="food-category-wrapper"
                >
                  <autocomplete
                    v-model="mealFormData.eating"
                    :label="toLatin(index)"
                    :items="food"
                    item-value="id"
                    color="white"
                    item-text="name"
                    return-object
                    chips
                    multiple
                    truncate-chips
                    chip-close-start
                    :selection-char-limit="42"
                    :label-min-selected="30"
                  >
                    <template #append-outer>
                      <v-btn
                        icon
                        small
                        color="primary"
                        @click.stop="openFoodDialog(food)"
                        :title="$t('add_new_meal')"
                      >
                        <v-icon small>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </autocomplete>
                </div>
              </div>
            </div>
          </div>

          <!-- OPCIJE -->
          <div class="section-card">
            <div class="section-header">
              <v-icon left small>mdi-cog</v-icon>
              <span>{{ $t("options") }}</span>
            </div>
            <div class="section-content">
              <label class="subsection-label">{{ $t("shifts") }}</label>
              <div class="checkbox-grid">
                <v-checkbox
                  v-model="selectedShifts"
                  :label="$t('1st_shift')"
                  :value="1"
                  hide-details
                  dense
                ></v-checkbox>
                <v-checkbox
                  v-model="selectedShifts"
                  :label="$t('2nd_shift')"
                  :value="2"
                  hide-details
                  dense
                ></v-checkbox>
                <v-checkbox
                  v-model="selectedShifts"
                  :label="$t('3rd_shift')"
                  :value="3"
                  hide-details
                  dense
                ></v-checkbox>
                <v-checkbox
                  v-model="selectedShifts"
                  :label="$t('4th_shift')"
                  :value="4"
                  hide-details
                  dense
                ></v-checkbox>
              </div>
              <v-alert v-if="hasValidationError" dense outlined type="error" class="mt-2">
                {{ $t("choose_shift") }}
              </v-alert>

              <label class="subsection-label mt-4">{{ $t("menu_type") }}</label>
              <div class="checkbox-grid">
                <v-checkbox
                  v-for="(menuType, index) in menuTypes"
                  :key="index"
                  v-model="mealFormData.type"
                  :label="$t(menuType.name)"
                  :value="menuType.id"
                  hide-details
                  dense
                ></v-checkbox>
              </div>

              <label class="subsection-label mt-4">{{ $t("label") }}</label>
              <div>
                <primary-button color="primary" small @click="isLabelDialogOpen = true">
                  <v-icon left small>mdi-tag-plus</v-icon>
                  {{ $t("label") }}
                </primary-button>
                <global-dialog-card
                  v-model="isLabelDialogOpen"
                  :title="$t('label')"
                  hide-actions
                  :max-width="500"
                >
                  <v-treeview
                    selectable
                    activatable
                    :items="availableLabels"
                    v-model="mealFormData.labels"
                    return-object
                    item-text="name"
                    item-key="id"
                  ></v-treeview>
                </global-dialog-card>
                <div
                  v-if="mealFormData.labels && mealFormData.labels.length"
                  class="mt-2"
                >
                  <v-chip
                    v-for="(label, index) in mealFormData.labels"
                    :key="index"
                    class="mr-2 mb-1"
                    close
                    small
                    @click:close="mealFormData.labels.splice(index, 1)"
                  >
                    {{ label.name }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </v-form>
      </v-tab-item>
    </v-tabs-items>

    <!-- Food Dialog -->
    <food-dialog
      v-model="isFoodDialogOpen"
      :edited-item="newFoodItem"
      :categories="categories"
      :read-only-category="true"
      @save="saveNewFood"
    />
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog";
import FoodDialog from "@/components/food/fields/dialogs/FoodDialog.vue";
import hasModel from "@/mixins/hasModel";
import labelService from "@/services/label";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasValidation from "@/mixins/hasValidation";
import mealService from "@/services/meal";
import { defaultFood } from "@/models/food";

export default {
  name: "MealEditDialog",
  components: {
    StandardDialog,
    FoodDialog,
  },
  mixins: [hasModel, hasSnackbar, hasValidation],
  props: {
    foods: {
      type: Object,
      default: () => ({}),
    },
    menuTypes: {
      type: Array,
      default: () => [],
    },
    editItem: {
      type: Object,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeLanguageTab: 0,
      mealFormData: {
        id: "",
        name: null,
        newSelectedName: null,
        description: null,
        type: [],
        price: null,
        groups: [],
        eating: [],
        quantity: 0,
        measure: "kg",
        labels: [],
        image_id: null,
        translations: [],
      },
      selectedShifts: [],
      hasValidationError: false,
      hasPriceError: false,
      isLabelDialogOpen: false,
      availableLabels: [],
      isFoodDialogOpen: false,
      newFoodItem: { ...defaultFood },
      selectedCategoryForNewFood: null,
    };
  },
  created() {
    this.loadAvailableLabels();
  },
  methods: {
    openFoodDialog(categoryFoods) {
      // Pronađi kategoriju po imenu
      if (!categoryFoods || categoryFoods.length === 0) {
        this.showErrorSnackbar(this.$t("no_foods_in_category"));
        return;
      }
      const category = categoryFoods[0]?.category;
      if (!category) {
        this.showErrorSnackbar(this.$t("category_not_found"));
        return;
      }

      this.selectedCategoryForNewFood = category;
      this.newFoodItem = {
        ...defaultFood,
        category,
      };
      this.isFoodDialogOpen = true;
    },
    async saveNewFood() {
      try {
        const foodData = {
          ...this.newFoodItem,
          category_id: this.newFoodItem.category.id,
        };
        const { data } = await mealService.createFood(foodData);
        // Dodaj novo jelo u listu jela za ovu kategoriju
        if (this.foods[this.selectedCategoryForNewFood.name]) {
          this.foods[this.selectedCategoryForNewFood.name].push(data.data);
        }

        // Automatski dodaj u eating
        if (!this.mealFormData.eating) {
          this.mealFormData.eating = [];
        }
        this.mealFormData.eating.push(data.data);

        this.showSuccessSnackbar(this.$t("meal_added_successfully"));
        this.isFoodDialogOpen = false;
        this.$emit("food-created", data.data);
      } catch (error) {
        this.showErrorSnackbar(error || this.$t("error_creating_meal"));
      }
    },
    loadAvailableLabels() {
      labelService
        .getLabels()
        .then((res) => {
          this.availableLabels = res.data;
        })
        .catch((error) => {
          this.showErrorSnackbar(error || this.$t("error_loading_labels"));
        });
    },
    populateFormWithMealData(meal) {
      const existingTranslations = meal.translations || [];
      const mergedTranslations = this.availableLanguages.map((lang) => {
        const found = existingTranslations.find((t) => t.code === lang.code);
        return found ? { ...found } : { code: lang.code, name: "", description: "" };
      });

      Object.assign(this.mealFormData, {
        id: meal.id,
        name: meal.name,
        one: meal.one ? 1 : 0,
        eating: meal.foods,
        description: meal.description,
        type: [...new Set(meal.type)],
        price: meal.price,
        groups: meal.groups,
        quantity: meal.quantity,
        measure: meal.measure,
        labels: meal.labels,
        translations: mergedTranslations,
      });
      this.selectedShifts = [
        meal.shift_one && 1,
        meal.shift_two && 2,
        meal.shift_three && 3,
        meal.shift_four && 4,
      ].filter(Boolean);
    },
    resetFormData() {
      Object.assign(this.mealFormData, {
        id: "",
        name: null,
        newSelectedName: null,
        description: null,
        type: [],
        price: null,
        groups: [],
        eating: [],
        quantity: null,
        measure: "",
        labels: [],
        image_id: null,
        translations: this.availableLanguages.map((lang) => ({
          code: lang.code,
          name: "",
        })),
      });
      this.selectedShifts = [];
      this.hasValidationError = false;
      this.hasPriceError = false;
    },
    submitMealForm() {
      // Cijena je obavezna samo za jela koja vide gosti (tip "guest_meal"): jelo bez cijene
      // bi se tamo prikazalo kao 0.00 i moglo da se naruči. Jela za firme (zaposleni) cijenu
      // često nemaju (ugovor, show_price), pa se za njih ništa ne traži.
      const price = parseFloat(String(this.mealFormData.price ?? "").replace(",", "."));
      this.hasPriceError = this.isGuestMeal && !(price > 0);
      this.hasValidationError = !this.selectedShifts.length;
      if (this.hasPriceError || this.hasValidationError) return;

      const submittedMealData = {
        id: this.mealFormData.id,
        quantity: this.mealFormData.quantity,
        measure: this.mealFormData.measure,
        name: this.mealFormData.newSelectedName || this.mealFormData.name,
        price: this.mealFormData.price,
        foods: this.mealFormData.eating,
        description: this.mealFormData.description,
        type: this.mealFormData.type,
        one: this.mealFormData.one ? 1 : 0,
        shift_one: this.selectedShifts.includes(1) ? 1 : 0,
        shift_two: this.selectedShifts.includes(2) ? 1 : 0,
        shift_three: this.selectedShifts.includes(3) ? 1 : 0,
        shift_four: this.selectedShifts.includes(4) ? 1 : 0,
        labels: this.mealFormData.labels,
        groups: this.mealFormData.groups,
        image_id: this.mealFormData.image_id || null,
        translations: this.mealFormData.translations,
      };
      if (this.editItem) {
        this.$emit("update-meal", submittedMealData);
      } else {
        // For new meal, add additional image structure
        submittedMealData.image = {
          name: "",
          restaurant_id: null,
          id: null,
          host: "",
        };
        this.$emit("add-meal", submittedMealData);
      }

      this.model = false;
    },
  },
  computed: {
    // jelo je za goste ako je među izabranim tipovima jelovnika onaj opšteg tipa "guest_meal"
    // (id 3 na backendu); dok se tipovi ne učitaju, ništa se ne smatra gostinjskim
    isGuestMeal() {
      const selected = (this.mealFormData.type || []).map(String);
      return this.menuTypes.some(
        (t) => t.general_type === "guest_meal" && selected.includes(String(t.id))
      );
    },
    availableLanguages() {
      // Filter languages based on restaurant settings (same logic as LanguageSelector)
      const restaurant =
        this.$store.state.restaurant || this.$store.state.company?.restaurant;
      const restaurantLanguages = restaurant?.settings?.language || [];

      const def = [
        {
          code: "default",
          text: "default",
          locale: "default",
          flag: "🇷",
          script: "default",
        },
      ];
      if (restaurantLanguages.length === 0) {
        // If no restaurant languages configured, return all available languages
        let data = this.getAvailableLanguages();
        return def.concat(data);
      }
      // Filter available languages to only include those configured for the restaurant
      let data = this.getAvailableLanguages().filter((lang) =>
        restaurantLanguages.includes(lang.code)
      );
      return def.concat(data);
    },
    dialogTitle() {
      if (this.editItem) {
        return `${this.$t("edit_meal")}: ${this.mealFormData.name || this.$t("meal")}`;
      }
      return this.$t("add_new_meal");
    },
    restaurant() {
      return this.$store.state.restaurant;
    },
  },
  watch: {
    editItem: {
      handler(mealToEdit) {
        if (mealToEdit) {
          this.populateFormWithMealData(mealToEdit);
        } else {
          this.resetFormData();
        }
      },
      immediate: true,
      deep: true,
    },
    model(isOpen) {
      // Reset when dialog closes
      if (!isOpen) {
        this.resetFormData();
        this.selectedShifts = [];
        this.hasValidationError = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.section-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-header {
  background: #f5f5f5;
  color: rgba(0, 0, 0, 0.7);
  padding: 12px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;

  .v-icon {
    color: #2196f3 !important;
  }

  span {
    margin-left: 8px;
  }
}

.section-content {
  padding: 20px;
  background: #fafafa;
}

.subsection-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  display: block;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.price-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.meal-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.food-category-wrapper {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
